<script lang="ts">
	import {
		DeviceBrowser,
		DeviceRPi,
		DeviceStatusListener,
		ProjectorPowerManager,
		type Device,
	} from "luxedo-data"
	import { Reactivity } from "luxedo-reactivity"
	import { LoadingSpinner } from "misc-ui"
	import { onDestroy, onMount } from "svelte"
	import type { Unsubscriber } from "svelte/store"

	interface Props {
		device: DeviceRPi | DeviceBrowser
		showControls?: boolean
	}

	let { device, showControls = true }: Props = $props()

	$effect.pre(() => {
		Reactivity.useProps(device, ["name"])
	})

	let deviceName = $derived(Reactivity.propOf(device, "name"))
	let deviceStatusListener = $derived(new DeviceStatusListener(device))

	$effect(() => {
		return deviceStatusListener.listen()
	})

	let deviceStatus = $derived(deviceStatusListener.statusText)
	let deviceStatusColor = $derived(deviceStatusListener.statusColor)

	let isChanging = $state(false)
	let canShowControls = $derived(device instanceof DeviceRPi)

	async function onWakeUp(e: MouseEvent) {
		if (!(device instanceof DeviceRPi)) return

		e.stopPropagation()
		await ProjectorPowerManager.setPower(device, "ON")
	}

	async function onSleep(e: MouseEvent) {
		if (!(device instanceof DeviceRPi)) return

		e.stopPropagation()
		await ProjectorPowerManager.setPower(device, "OFF")
	}

	let unsubscriber: Unsubscriber

	onMount(() => {
		unsubscriber = ProjectorPowerManager.subscribe((ctx) => {
			isChanging = ctx[device.id]?.isChanging ?? false
		})
	})

	onDestroy(() => {
		unsubscriber()
	})

	$inspect("PREVIEW ONE", showControls)
</script>

<h1 class="device-name">{deviceName}</h1>
<span class="projector-status">
	<div
		class="projector-status-indicator"
		class:isChanging
		style="--color-status: {deviceStatusColor};"
	></div>
	{deviceStatus}
	{#if showControls && canShowControls}
		<div class="device-control">
			{#if isChanging || deviceStatus === "Waking up..." || deviceStatus === "Cooling down..."}
				<LoadingSpinner height="1em" color={"var(--color-main)"} />
			{:else if deviceStatus === "Asleep"}
				<button class="control-button small outline-button" onclick={onWakeUp} disabled={isChanging}
					>Wake</button
				>
			{:else if deviceStatus === "Ready"}
				<button
					class="control-button small outline-button alt sleep-button"
					onclick={onSleep}
					disabled={isChanging}>Sleep</button
				>
			{/if}
		</div>
	{/if}
</span>

<style>
</style>
